import { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  DialogContent,
  FormControl,
  Chip,
  FormHelperText,
  Grid,
  IconButton,
  OutlinedInput,
  Stack,
  TextField,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useAppSelector } from 'src/app/hooks';
import TurnedInNotIcon from '@mui/icons-material/TurnedInNot';
import * as Yup from 'yup';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { IAutoClippingPayload } from 'src/models/onlineMedia';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { withStyles } from '@mui/styles';
import useOnlineMedia from 'src/services/useOnlineMedia';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import moment from 'moment';
import { TMediaType } from 'src/models/offlineMedia';
import useOfflineMedia from 'src/services/useOfflineMedia';
import { useAlert } from 'src/hooks/useAlert';
import { CustomGraphQLError } from 'src/models/general';
import { regexAllLanguagesExceptDoubleQuote } from 'src/constants';
import Information from 'src/components/Information';
import { usePhraseInputInfo } from 'src/hooks/usePhraseInputInfo';
import QuotaWarning from './QuotaWarning';
import { t } from 'i18next';
import NotAllowedTooltip from 'src/components/NotAllowedTooltip';

const CustomAutocomplete = withStyles({
  inputRoot: {
    '&[class*="MuiOutlinedInput-root"][class*="MuiOutlinedInput-marginDense"] .MuiAutocomplete-input':
      {
        padding: 0
      }
  }
})((props: any) => <Autocomplete {...props} />);

export const EMPTY_PHRASE_ERROR = "phrase can't be empty";

function validationSchema() {
  return Yup.object({
    name: Yup.string().required(t('name is required')),
    startingDate: Yup.date().required(t('date is required')),
    phrasesList: Yup.array()
      .of(
        Yup.array()
          .of(
            Yup.string()
              .min(2, ` ${t('phrase must contain at least two characters')}`)
              .matches(
                regexAllLanguagesExceptDoubleQuote,
                ` ${t('phrase must not contain symbols')}`
              )
          )
          .min(1, t(EMPTY_PHRASE_ERROR))
      )
      .min(1, t(EMPTY_PHRASE_ERROR))
      .required(t(EMPTY_PHRASE_ERROR)),
    excludePhrases: Yup.array().of(
      Yup.string()
        .min(2, ` ${t('phrase must contain at least two characters')}`)
        .matches(
          regexAllLanguagesExceptDoubleQuote,
          ` ${t('phrase must not contain symbols')}`
        )
    )
  });
}

export function getPhraseListError(
  phraseListError: unknown
): string | Set<string> | undefined {
  if (typeof phraseListError === 'string') {
    return phraseListError;
  }

  if (Array.isArray(phraseListError)) {
    for (const item of phraseListError) {
      const nestedError = getPhraseListError(item);

      if (nestedError) {
        return nestedError;
      }
    }
  }

  return undefined;
}

interface IProps {
  clippingType?: TMediaType;
  onCompletedSubmit?: () => void;
}

const ModalAddAutoClippingRetail = ({
  clippingType = 'onlineMedia',
  onCompletedSubmit = () => {}
}: IProps) => {
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    resetForm();
  };

  const theme = useTheme();
  const matchesUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const { handleClickAlert } = useAlert();
  const {
    id: projectId,
    createdAt,
    customerType,
    role: userRole,
    onBoarding: { onlineMediaObjectQuota, onlineMediaObjectQuotaUsed }
  } = useAppSelector((state) => state.users.project);
  const clippingList = useAppSelector(
    (state) => state.storeOnlineMediaStream.clippingList.data
  );
  const booleanQuery = useAppSelector(
    (state) => state.chatNow.copiedBooleanQuery
  );
  const { getClippingsV5, addClippingV5, responseAddClippingV5 } =
    useOnlineMedia();
  const { addClippingOfflineMedia } = useOfflineMedia();

  const isRetail = customerType === 'b2c';
  const isMember = userRole === 'member';

  const {
    handlePhraseInputFocus,
    handlePhraseInputBlur,
    handlePhraseInputChange,
    showPhraseInputInfo
  } = usePhraseInputInfo();

  const formik = useFormik<IAutoClippingPayload>({
    initialValues: {
      name: '',
      phrasesList: [[]],
      startingDate: new Date(),
      excludePhrases: []
    },
    validationSchema: validationSchema(),
    onSubmit: (values) => {
      formik.setSubmitting(true);
      const formattedDate = moment(
        values.startingDate.setHours(0, 0, 0, 0)
      ).format('YYYY-MM-DD HH:mm:ss');

      if (clippingType === 'onlineMedia') {
        addClippingV5({
          variables: {
            ...values,
            projectId,
            startingDate: formattedDate
          }
        }).then((data) => {
          formik.setSubmitting(false);
          if (data.errors) {
            const errorResponse = data.errors as CustomGraphQLError[];
            const [message] = errorResponse[0].extensions.reason.failures;
            handleClickAlert({
              message,
              horizontal: 'center',
              vertical: 'top',
              severity: 'error'
            });
            return;
          }
          handleClose();
          getClippingsV5({
            projectId,
            page: clippingList.page,
            limit: clippingList.limit,
            name: ''
          });
        });
        return;
      }

      addClippingOfflineMedia({
        variables: {
          ...values,
          projectId,
          startingDate: formattedDate,
          categoryArticle: clippingType
        },
        onCompleted: () => {
          formik.setSubmitting(false);
          handleClose();
          onCompletedSubmit();
        }
      });
    }
  });

  const {
    handleChange,
    setFieldValue,
    handleSubmit,
    errors,
    values,
    touched,
    resetForm,
    isSubmitting
  } = formik;

  const handleClickPasteBooleanQuery = () => {
    setFieldValue('phrasesList', booleanQuery.phrasesList);
    if (clippingType === 'onlineMedia')
      setFieldValue('excludePhrases', booleanQuery.excludePhrases);
  };

  const errorPhraseList = getPhraseListError(errors?.phrasesList);
  const errorExcludePhrases = new Set(errors?.excludePhrases);
  const createdDate = moment(createdAt);

  const minDate = new Date(
    createdDate.year(),
    createdDate.month() - 1,
    createdDate.date()
  );

  const contactLink =
    'https://api.whatsapp.com/send/?phone=628131134707&text=Halo%20NoLimit%2C%20saya%20ingin%20bertanya%20tentang%20produk%20NoLimit.%20Terima%20kasih.';

  const addClippingButton = (
    <Button
      variant="contained"
      sx={{
        borderRadius: '50px',
        height: theme.spacing(5),
        minWidth: theme.spacing(6),
        px: 3,
        background: '#000000',
        color: theme.palette.common.white,
        whiteSpace: 'nowrap',
        boxShadow: 'none',
        '&.MuiButtonBase-root': {
          borderRadius: '50px'
        },
        '&:hover': {
          background: '#333333',
          boxShadow: 'none'
        }
      }}
      onClick={!isMember ? handleClickOpen : undefined}
      startIcon={<TurnedInNotIcon />}
    >
      {t('make clipping')}
    </Button>
  );

  return (
    <>
      {isMember ? (
        <NotAllowedTooltip>{addClippingButton}</NotAllowedTooltip>
      ) : (
        addClippingButton
      )}

      <Dialog
        onClose={handleClose}
        open={open}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 3, pt: 2.5, pb: 0 }}
        >
          <Typography variant="h3" fontWeight={700} fontSize={20}>
            {t('new clipping')}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            size="small"
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>

        <DialogContent sx={{ px: 3, pt: 2, pb: 3 }}>
          <FormikProvider value={formik}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                {onlineMediaObjectQuotaUsed === onlineMediaObjectQuota &&
                  clippingType === 'onlineMedia' && (
                    <QuotaWarning
                      quotaUsed={onlineMediaObjectQuotaUsed}
                      quota={onlineMediaObjectQuota}
                      contactLink={contactLink}
                    />
                  )}
                <Grid item xs={12}>
                  <Typography
                    sx={{ mb: 0.5, fontWeight: 700, fontSize: '13px' }}
                  >
                    {t('clipping name')}
                  </Typography>
                  <FormControl fullWidth size="medium">
                    <OutlinedInput
                      name="name"
                      onChange={handleChange}
                      placeholder={t(
                        'type keywords related to the news you want to track'
                      )}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.preventDefault();
                      }}
                    />
                    <FormHelperText error sx={{ ml: 0 }}>
                      {errors.name && touched.name && errors.name}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    sx={{
                      bgcolor: '#FAFAFA',
                      border: '1px solid #E0E0E0',
                      borderRadius: '12px',
                      p: 2.5
                    }}
                  >
                    <Typography
                      sx={{ mb: 0.5, fontWeight: 700, fontSize: '13px' }}
                    >
                      {t('starting date')}
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DesktopDatePicker
                        value={values.startingDate}
                        onChange={(newValue) => {
                          setFieldValue('startingDate', newValue);
                        }}
                        renderInput={(params) => (
                          <TextField {...params} fullWidth />
                        )}
                        minDate={isRetail ? minDate : undefined}
                      />
                    </LocalizationProvider>

                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ mt: 2.5, mb: 1.5 }}
                    >
                      <Typography sx={{ fontWeight: 700, fontSize: '13px' }}>
                        {t('phrase')}
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleClickPasteBooleanQuery}
                        endIcon={
                          <Information
                            color="#fff"
                            content={
                              <Typography
                                variant="body1"
                                color="#fff"
                                fontSize={12}
                              >
                                {t(
                                  'paste boolean query from chat ai into the phrase field.'
                                )}
                              </Typography>
                            }
                          />
                        }
                        sx={{
                          borderRadius: theme.spacing(1),
                          px: 2,
                          bgcolor: theme.palette.primary.main,
                          '&:hover': { bgcolor: theme.palette.primary.dark }
                        }}
                      >
                        {t('paste boolean query')}
                      </Button>
                    </Stack>

                    <FieldArray name="phrasesList" validateOnChange={false}>
                      {({ remove }) => (
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            maxHeight: 128,
                            overflowY: 'auto',
                            overflowX: 'hidden',
                            pb: 1,
                            pr: 1
                          }}
                        >
                          {values.phrasesList.map((rows, index) => (
                            <Stack
                              key={index}
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              sx={{
                                width: matchesUpMd
                                  ? 'calc(50% - 8px)'
                                  : 'calc(100%)'
                              }}
                            >
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <CustomAutocomplete
                                  multiple
                                  size="small"
                                  options={[]}
                                  defaultValue={[]}
                                  freeSolo
                                  value={rows}
                                  renderTags={(vals: Array<any>, getTagProps) =>
                                    vals.map((option: string, i: number) => (
                                      <Chip
                                        key={`${option}-${i}-phrases`}
                                        variant="outlined"
                                        label={
                                          <Typography>{option}</Typography>
                                        }
                                        size="small"
                                        {...getTagProps({ index: i })}
                                      />
                                    ))
                                  }
                                  onChange={(_e, value) => {
                                    setFieldValue(
                                      `phrasesList[${index}]`,
                                      value
                                    );
                                  }}
                                  forcePopupIcon={false}
                                  disableClearable
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      onFocus={(event) =>
                                        handlePhraseInputFocus(event, index)
                                      }
                                      onBlur={handlePhraseInputBlur}
                                      onChange={(
                                        event: React.ChangeEvent<HTMLInputElement>
                                      ) =>
                                        handlePhraseInputChange(event, index)
                                      }
                                      variant="outlined"
                                      size="small"
                                      autoComplete="off"
                                      placeholder={t('separated by enter')}
                                      sx={{ bgcolor: 'white', borderRadius: 1 }}
                                      onPaste={(event) => {
                                        let keywords =
                                          event.clipboardData.getData('text');
                                        keywords = keywords.replace(
                                          /[\u201C\u201D]/g,
                                          '"'
                                        );
                                        const trimmed = keywords
                                          .split(',')
                                          .filter((w) => w.trim().length > 1)
                                          .map((w) => w.trim());
                                        setFieldValue(`phrasesList[${index}]`, [
                                          ...values.phrasesList[index],
                                          ...trimmed
                                        ]);
                                      }}
                                    />
                                  )}
                                />
                              </Box>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.75}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => remove(index)}
                                  disabled={values.phrasesList.length === 1}
                                >
                                  <DeleteIcon fontSize="small" color="error" />
                                </IconButton>
                                <Typography
                                  sx={{
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap'
                                  }}
                                  onClick={() => {
                                    setFieldValue('phrasesList', [
                                      ...values.phrasesList,
                                      []
                                    ]);
                                  }}
                                >
                                  {t('or')}
                                </Typography>
                              </Stack>
                            </Stack>
                          ))}
                        </Box>
                      )}
                    </FieldArray>
                    {showPhraseInputInfo ? (
                      <FormHelperText sx={{ ml: 0 }}>
                        {t('separated by enter')}
                      </FormHelperText>
                    ) : null}
                    <FormHelperText error sx={{ ml: 0 }}>
                      {errors.phrasesList &&
                        touched.phrasesList &&
                        errorPhraseList}
                    </FormHelperText>

                    {clippingType === 'onlineMedia' && (
                      <>
                        <Typography
                          variant="body2"
                          sx={{
                            mt: 2,
                            mb: 0.5,
                            fontWeight: 600,
                            fontSize: '12px'
                          }}
                        >
                          {t('exclude')}
                        </Typography>
                        <CustomAutocomplete
                          multiple
                          id="keyword-excludes"
                          size="small"
                          options={[]}
                          defaultValue={[]}
                          freeSolo
                          value={values.excludePhrases}
                          renderTags={(vals: Array<any>, getTagProps) =>
                            vals.map((option: string, index: number) => (
                              <Chip
                                key={`${option}-${index}-excludes`}
                                variant="outlined"
                                label={<Typography>{option}</Typography>}
                                size="small"
                                {...getTagProps({ index })}
                              />
                            ))
                          }
                          onChange={(_e, value) => {
                            setFieldValue('excludePhrases', value);
                          }}
                          forcePopupIcon={false}
                          disableClearable
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              name="excludes"
                              variant="outlined"
                              size="small"
                              autoComplete="off"
                              placeholder={t('press enter to separate')}
                              sx={{ bgcolor: 'white', borderRadius: 1 }}
                              onPaste={(event) => {
                                const keywords =
                                  event.clipboardData.getData('text');
                                const trimmed = keywords
                                  .split(',')
                                  .filter((w) => w.trim().length > 1)
                                  .map((w) => w.trim());
                                setFieldValue('excludePhrases', [
                                  ...values.excludePhrases,
                                  ...trimmed
                                ]);
                              }}
                            />
                          )}
                        />
                        <FormHelperText error sx={{ ml: 0 }}>
                          {errors.excludePhrases &&
                            touched.excludePhrases &&
                            errorExcludePhrases}
                        </FormHelperText>
                      </>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="center"
                    gap={2}
                    sx={{ mt: 1 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      sx={{
                        borderRadius: '50px',
                        px: 5,
                        py: 1.2,
                        minWidth: 130,
                        fontSize: '15px',
                        fontWeight: 600
                      }}
                    >
                      {t('clear')}
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={
                        responseAddClippingV5.loading ||
                        isSubmitting ||
                        onlineMediaObjectQuotaUsed === onlineMediaObjectQuota
                      }
                      sx={{
                        borderRadius: '50px',
                        px: 5,
                        py: 1.2,
                        minWidth: 130,
                        fontSize: '15px',
                        fontWeight: 600,
                        bgcolor: theme.palette.primary.main,
                        '&:hover': { bgcolor: theme.palette.primary.dark }
                      }}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={20} />
                      ) : (
                        t('save')
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </FormikProvider>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ModalAddAutoClippingRetail;
